import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  CheckCircle2,
  Mail,
  PackageOpen,
  RefreshCw,
  Send,
  Truck,
  XCircle,
} from "lucide-react";
import { toast } from "sonner";

import AppNav from "@/components/app/AppNav";
import { api, formatApiError } from "@/api/client";

// V1.1d — /mailed-letters
//
// User-facing list of every dispute letter the user has mailed via Lob
// (stub mode while no live API key is configured). Click a row to see the
// tracking timeline + Refresh/Cancel buttons.

const STATUS_META = {
  draft:                  { label: "Draft",          icon: PackageOpen,  tone: "neutral" },
  submitted:              { label: "Submitted",      icon: Send,         tone: "warning" },
  in_transit:             { label: "In transit",     icon: Truck,        tone: "accent"  },
  processed_for_delivery: { label: "At post office", icon: Truck,        tone: "accent"  },
  delivered:              { label: "Delivered",      icon: CheckCircle2, tone: "success" },
  returned:               { label: "Returned",       icon: XCircle,      tone: "danger"  },
  canceled:               { label: "Canceled",       icon: XCircle,      tone: "danger"  },
  failed:                 { label: "Failed",         icon: XCircle,      tone: "danger"  },
};

const STATUS_STYLES = {
  neutral: {
    color:  "var(--ds-color-ink-muted)",
    bg:     "var(--ds-color-surface-muted)",
    border: "var(--ds-color-border-strong)",
  },
  accent: {
    color:  "var(--ds-color-accent)",
    bg:     "var(--ds-color-accent-soft)",
    border: "var(--ds-color-accent)",
  },
  success: {
    color:  "var(--ds-color-success)",
    bg:     "var(--ds-color-success-soft)",
    border: "var(--ds-color-success)",
  },
  warning: {
    color:  "var(--ds-color-warning)",
    bg:     "var(--ds-color-warning-soft)",
    border: "var(--ds-color-warning)",
  },
  danger: {
    color:  "var(--ds-color-danger)",
    bg:     "var(--ds-color-danger-soft)",
    border: "var(--ds-color-danger)",
  },
};

function statusStyle(status) {
  const meta = STATUS_META[status] || STATUS_META.submitted;
  return { meta, styles: STATUS_STYLES[meta.tone] };
}


export default function MailedLettersPage() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openId, setOpenId] = useState(null);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/mailed-letters");
      setItems(data || []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { reload(); }, [reload]);

  return (
    <main className="min-h-screen text-[var(--ds-color-ink)] pb-20 bg-[var(--ds-color-background)]" data-testid="mailed-letters-page">
      <AppNav />
      <div className="mx-auto max-w-5xl px-4 md:px-8 pt-10">
          <Link
            to="/dispute-letters"
            className="inline-flex items-center gap-1.5 text-[13px] font-medium text-[var(--ds-color-ink-muted)] hover:text-[var(--ds-color-ink)] transition-colors"
            style={{ transitionDuration: "var(--ds-duration-fast)" }}
          >
            <ArrowLeft className="w-3.5 h-3.5" aria-hidden /> Back to letters
          </Link>

        <header className="mt-6 flex flex-wrap items-center gap-3">
          <div className="min-w-0">
            <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-[var(--ds-color-ink-muted)]">
              Credit Repair · Mailed
            </div>
            <h1
              className="mt-2 text-[var(--ds-color-ink)] leading-tight"
              style={{
                fontFamily: "var(--ds-font-display)",
                fontWeight: 600,
                letterSpacing: "-0.02em",
                fontSize: "clamp(1.75rem, 3vw, 2.5rem)",
              }}
            >
              Mailed letters.
            </h1>
          </div>
          <span
            className="inline-flex items-center px-2.5 py-1 rounded-[var(--ds-radius-pill)] text-[10px] uppercase tracking-[0.18em] font-semibold"
            style={{
              background: "var(--ds-color-accent-soft)",
              border: "1px solid var(--ds-color-accent)",
              color: "var(--ds-color-accent)",
            }}
          >
            Lob · Stub
          </span>
          <button
            type="button"
            data-testid="mailed-letters-refresh"
            onClick={reload}
            className="ml-auto inline-flex items-center gap-1.5 px-3 py-1.5 rounded-[var(--ds-radius-md)] text-[12px] font-semibold transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ds-color-focus)] focus-visible:ring-offset-2"
            style={{
              background: "var(--ds-color-surface-muted)",
              border: "1px solid var(--ds-color-border-strong)",
              color: "var(--ds-color-ink)",
              transitionDuration: "var(--ds-duration-fast)",
            }}
          >
            <RefreshCw className="w-3.5 h-3.5" aria-hidden /> Refresh
          </button>
        </header>

        <p className="mt-4 text-[13px] leading-relaxed text-[var(--ds-color-ink-muted)] max-w-2xl">
          Every dispute letter you&rsquo;ve mailed shows up here with live tracking.
          While we&rsquo;re in sandbox mode, deliveries are simulated — real letters
          aren&rsquo;t actually printed or mailed until an admin enables live mode.
        </p>

        <section
          data-testid="mailed-letters-list"
          className="mt-8 rounded-[var(--ds-radius-lg)] overflow-hidden"
          style={{
            background: "var(--ds-color-surface)",
            border: "1px solid var(--ds-color-border)",
            boxShadow: "var(--ds-shadow-card)",
          }}
        >
          {loading ? (
            <div className="px-5 py-8 text-[13px] text-[var(--ds-color-ink-muted)] text-center">Loading…</div>
          ) : items.length === 0 ? (
            <div className="px-5 py-10 text-center">
              <Mail className="w-6 h-6 mx-auto mb-2 text-[var(--ds-color-ink-muted)]" aria-hidden />
              <p className="text-[13px] leading-relaxed text-[var(--ds-color-ink-muted)]">
                You haven&rsquo;t mailed any letters yet. Open a finalized dispute
                letter and use &ldquo;Mail this letter&rdquo;.
              </p>
            </div>
          ) : (
            <ul className="divide-y divide-[var(--ds-color-border)]">
              {items.map((row) => {
                const { meta, styles } = statusStyle(row.status);
                const Icon = meta.icon;
                return (
                  <li
                    key={row.id}
                    data-testid={`mailed-letter-row-${row.id}`}
                    className="px-5 py-4 flex flex-wrap items-center gap-4 cursor-pointer transition-colors hover:bg-[var(--ds-color-surface-muted)]"
                    onClick={() => setOpenId(row.id)}
                    style={{ transitionDuration: "var(--ds-duration-fast)" }}
                  >
                    <div className="flex items-center gap-3 min-w-0 flex-1">
                      <span
                        className="inline-flex w-8 h-8 items-center justify-center rounded-[var(--ds-radius-md)] flex-shrink-0"
                        style={{ background: styles.bg, border: `1px solid ${styles.border}`, color: styles.color }}
                      >
                        <Icon className="w-4 h-4" aria-hidden />
                      </span>
                      <div className="min-w-0">
                        <div className="text-[14px] text-[var(--ds-color-ink)] truncate">
                          {row.to_address?.name || "Recipient"} · {row.to_address?.city}, {row.to_address?.state}
                        </div>
                        <div className="text-[11px] text-[var(--ds-color-ink-muted)] truncate">
                          {row.lob_letter_id} · {row.lob_mode === "stub" ? "Sandbox" : "Live"} · ${((row.price_cents || 0) / 100).toFixed(2)}
                        </div>
                      </div>
                    </div>
                    <span
                      className="text-[10px] uppercase tracking-[0.18em] px-2.5 py-1 rounded-[var(--ds-radius-pill)]"
                      style={{ background: styles.bg, border: `1px solid ${styles.border}`, color: styles.color }}
                    >
                      {meta.label}
                    </span>
                    <span className="text-[11px] text-[var(--ds-color-ink-muted)] w-28 text-right">
                      {row.submitted_at ? new Date(row.submitted_at).toLocaleDateString() : "—"}
                    </span>
                  </li>
                );
              })}
            </ul>
          )}
        </section>
      </div>

      {openId && (
        <MailedLetterDrawer
          mailedId={openId}
          onClose={() => setOpenId(null)}
          onChanged={reload}
        />
      )}
    </main>
  );
}


function MailedLetterDrawer({ mailedId, onClose, onChanged }) {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/mailed-letters/${mailedId}`);
      setDetail(data);
    } catch (e) {
      toast.error(formatApiError(e));
      onClose();
    } finally {
      setLoading(false);
    }
  }, [mailedId, onClose]);

  useEffect(() => { load(); }, [load]);

  async function refresh() {
    setBusy(true);
    try {
      const { data } = await api.post(`/mailed-letters/${mailedId}/refresh-status`);
      setDetail(data);
      toast.success("Status refreshed.");
      onChanged?.();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  }

  async function cancel() {
    if (!window.confirm("Cancel this letter? It will not be mailed. This is only possible while the letter is still draft or just submitted.")) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/mailed-letters/${mailedId}/cancel`);
      setDetail(data);
      toast.success("Letter canceled.");
      onChanged?.();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  }

  const { meta, styles } = detail ? statusStyle(detail.status) : statusStyle("submitted");
  const cancelable = detail && ["draft", "submitted"].includes(detail.status);

  return (
    <div
      data-testid="mailed-letter-drawer"
      className="fixed inset-0 flex justify-end bg-black/50"
      style={{ zIndex: "var(--ds-z-modal)" }}
      onClick={onClose}
    >
      <div
        className="w-full max-w-xl h-full overflow-y-auto"
        style={{ background: "var(--ds-color-surface)", borderLeft: "1px solid var(--ds-color-border)" }}
        onClick={(e) => e.stopPropagation()}
      >
        <header
          className="sticky top-0 flex items-center justify-between px-5 py-4 border-b border-[var(--ds-color-border)]"
          style={{ background: "var(--ds-color-surface)", zIndex: 1 }}
        >
          <div className="min-w-0">
            <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-[var(--ds-color-accent)]">Mailed letter</div>
            <div
              className="text-[var(--ds-color-ink)] truncate"
              style={{ fontFamily: "var(--ds-font-display)", fontWeight: 600, fontSize: "1rem" }}
            >
              {detail?.lob_letter_id || "…"}
            </div>
          </div>
          <button
            type="button"
            data-testid="mailed-letter-drawer-close"
            onClick={onClose}
            className="text-[var(--ds-color-ink-muted)] hover:text-[var(--ds-color-ink)] text-xl px-2 transition-colors"
            style={{ transitionDuration: "var(--ds-duration-fast)" }}
            aria-label="Close"
          >
            ×
          </button>
        </header>

        {loading || !detail ? (
          <div className="p-6 text-[13px] text-[var(--ds-color-ink-muted)]">Loading…</div>
        ) : (
          <div className="p-5 space-y-5">
            <div className="flex items-center gap-2 flex-wrap">
              <span
                className="text-[10px] uppercase tracking-[0.18em] px-2.5 py-1 rounded-[var(--ds-radius-pill)]"
                style={{ background: styles.bg, border: `1px solid ${styles.border}`, color: styles.color }}
              >
                {meta.label}
              </span>
              <span className="text-[11px] text-[var(--ds-color-ink-muted)]">
                {detail.lob_mode === "stub" ? "Sandbox (simulated)" : "Live"}
              </span>
              <span className="text-[11px] text-[var(--ds-color-ink-muted)]">
                ${((detail.price_cents || 0) / 100).toFixed(2)}
              </span>
            </div>

            <div className="flex flex-wrap items-center gap-2">
              <button
                type="button"
                data-testid="mailed-letter-drawer-refresh"
                onClick={refresh}
                disabled={busy}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-[var(--ds-radius-md)] text-[12px] font-semibold transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ds-color-focus)] focus-visible:ring-offset-2"
                style={{
                  background: "var(--ds-color-surface-muted)",
                  border: "1px solid var(--ds-color-border-strong)",
                  color: "var(--ds-color-ink)",
                  opacity: busy ? 0.6 : 1,
                  transitionDuration: "var(--ds-duration-fast)",
                }}
              >
                <RefreshCw className="w-3.5 h-3.5" aria-hidden /> Refresh status
              </button>
              {cancelable && (
                <button
                  type="button"
                  data-testid="mailed-letter-drawer-cancel"
                  onClick={cancel}
                  disabled={busy}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-[var(--ds-radius-md)] text-[12px] font-semibold transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ds-color-focus)] focus-visible:ring-offset-2"
                  style={{
                    border: "1px solid var(--ds-color-danger)",
                    color: "var(--ds-color-danger)",
                    transitionDuration: "var(--ds-duration-fast)",
                  }}
                >
                  <XCircle className="w-3.5 h-3.5" aria-hidden /> Cancel mailing
                </button>
              )}
            </div>

            <dl className="grid grid-cols-2 gap-3 text-xs">
              <DetailField label="Recipient" value={`${detail.to_address?.name || ""} · ${detail.to_address?.address_line1 || ""}`} />
              <DetailField label="Sent from" value={`${detail.from_address?.address_line1 || ""}`} />
              <DetailField label="City/State" value={`${detail.to_address?.city || ""}, ${detail.to_address?.state || ""} ${detail.to_address?.zip || ""}`} />
              <DetailField label="Mail type" value={detail.options?.mail_type === "usps_first_class" ? "USPS First Class" : "USPS Standard"} />
              <DetailField label="Submitted" value={detail.submitted_at ? new Date(detail.submitted_at).toLocaleString() : "—"} />
              <DetailField label="ETA" value={detail.expected_delivery_date ? new Date(detail.expected_delivery_date).toLocaleDateString() : "—"} />
              {detail.actual_delivery_date && (
                <DetailField label="Delivered" value={new Date(detail.actual_delivery_date).toLocaleString()} />
              )}
              <DetailField label="Last sync" value={detail.last_synced_at ? new Date(detail.last_synced_at).toLocaleString() : "—"} />
            </dl>

            <div data-testid="mailed-letter-drawer-timeline">
              <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-[var(--ds-color-ink-muted)] mb-2">
                Tracking timeline
              </div>
              <ol className="space-y-3 relative pl-5 border-l border-[var(--ds-color-border)]">
                {(detail.tracking_events || []).map((ev, i) => (
                  <li key={i} className="relative">
                    <span
                      className="absolute -left-[26px] top-1 w-3 h-3 rounded-[var(--ds-radius-pill)]"
                      style={{ background: styles.color }}
                    />
                    <div className="text-[13px] text-[var(--ds-color-ink)]">{ev.name}</div>
                    <div className="text-[11px] text-[var(--ds-color-ink-muted)]">
                      {ev.time ? new Date(ev.time).toLocaleString() : ""}
                      {ev.location ? ` · ${ev.location}` : ""}
                    </div>
                  </li>
                ))}
                {(!detail.tracking_events || detail.tracking_events.length === 0) && (
                  <li className="text-[13px] text-[var(--ds-color-ink-muted)]">No events yet — refresh in a moment.</li>
                )}
              </ol>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}


function DetailField({ label, value }) {
  return (
    <div>
      <div className="text-[10px] font-semibold uppercase tracking-[0.14em] text-[var(--ds-color-ink-muted)] mb-1">{label}</div>
      <div className="text-[13px] text-[var(--ds-color-ink)] break-words">{value}</div>
    </div>
  );
}
